/**
 * Platform detection utilities for Capacitor
 * 
 * Provides helper functions to detect the current platform (iOS, Android, Web)
 * and check for native plugin availability.
 */
import { Capacitor } from '@capacitor/core';

/**
 * Whether the app is running in a native container (iOS or Android)
 */
export const isNativeApp = Capacitor.isNativePlatform();

/**
 * Whether the app is running on iOS
 */
export const isIOS = Capacitor.getPlatform() === 'ios';

/**
 * Whether the app is running on Android
 */
export const isAndroid = Capacitor.getPlatform() === 'android';

/**
 * Whether the app is running in a web browser
 */
export const isWeb = Capacitor.getPlatform() === 'web';

/**
 * Check if a specific Capacitor plugin is available
 */
export const hasPlugin = (pluginName: string): boolean => {
  return Capacitor.isPluginAvailable(pluginName);
};

/**
 * Get the current platform name
 */
export const getPlatformName = (): 'ios' | 'android' | 'web' => {
  return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
};

/**
 * Get platform-specific information for debugging
 */
export const getPlatformInfo = () => ({
  platform: getPlatformName(),
  isNative: isNativeApp,
  isIOS,
  isAndroid,
  isWeb,
  plugins: {
    app: hasPlugin('App'),
    keyboard: hasPlugin('Keyboard'),
    pushNotifications: hasPlugin('PushNotifications'),
    browser: hasPlugin('Browser'),
    statusBar: hasPlugin('StatusBar'),
    splashScreen: hasPlugin('SplashScreen'),
    preferences: hasPlugin('Preferences'),
    haptics: hasPlugin('Haptics'),
    share: hasPlugin('Share'),
  }
});
